import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  getChartModeTypes,
  getDefaultChartMode
} from '@aytm/app/stats/page/question-views/md/components/chart/lib/chartModes'
import { calcHcAnswers } from '@aytm/app/stats/page/question-views/md/components/chart/lib/hcAnswers'
import { calcPlotLines } from '@aytm/app/stats/page/question-views/md/components/chart/lib/plotLines'
import { getYAxisTitle } from '@aytm/app/stats/page/question-views/md/components/chart/lib/viewModes'

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ChartNote from '@aytm/app/stats/page/question-views/vk/components/chart-note/ChartNote'
import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'

const MIN_COMPLETES = 400

const TOOLTIP_TEXT =
  'MaxDiff is a very efficient method to predict a price range/optimal price perception for a new ' +
  "product or service. Please remember that it only reports what the customers' expectations of " +
  "the price will be. It is also very sensitive to number of completes so don't base any " +
  'significant decisions on predictions with fewer responses than 400.'

// Low-completes warning, ported from the question template. Renders through the shared
// ChartNote in its error variant (same as the Vankonan chart's 400-completes note); the help
// text is a self-contained Popup rather than the template's jQuery-initialised tooltip.
//
// The gate is kept exactly as the template had it — note `null === badData` is a strict null
// check, so an absent or boolean bad-data flag suppresses the note.
const shouldShowChartNote = answer =>
  answer != null &&
  'undefined' !== typeof answer.total &&
  (answer.total || 0) < MIN_COMPLETES &&
  null === answer.badData

const MaxDiffChart = ({
  questionId,
  questionKey,
  qType,
  batches,
  answer,
  sortOrder,
  viewMode,
  itemsPerSet,
  chartMode: chartModeProp,
  modelChartMode,
  globalChartMode,
  onChartModeChange,
  onZoomChange,
  zoomLevel,
  globalZoomLevel,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  confidenceLevels,
  features,
  onChartReady
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        modelChartMode,
        globalChartMode,
        batches
      }),
    [chartModeProp, modelChartMode, globalChartMode, batches]
  )

  const chartModeTypes = useMemo(() => getChartModeTypes(batches), [batches])

  const { hcAnswers, yAxisLimits } = useMemo(
    () => calcHcAnswers({ answer, viewMode, chartMode }),
    [answer, viewMode, chartMode]
  )

  const plotLines = useMemo(() => calcPlotLines(viewMode, itemsPerSet), [viewMode, itemsPerSet])
  const yAxisTitle = useMemo(() => getYAxisTitle(viewMode, itemsPerSet), [viewMode, itemsPerSet])

  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <>
      <ChartContainer
        chartRef={chartRef}
        questionId={questionId}
        questionKey={questionKey}
        qType={qType}
        answerVariants={batches}
        answer={hcAnswers}
        sortOrder={sortOrder}
        chartMode={chartMode}
        selectorChartModeValue={chartMode}
        chartModeOverride={chartModeProp}
        globalChartMode={globalChartMode}
        chartModeTypes={chartModeTypes}
        viewOptionsDisabled={viewOptionsDisabled}
        viewMode={viewMode}
        yAxisTitle={yAxisTitle}
        yAxisLimits={yAxisLimits}
        plotLines={plotLines}
        zoomType={true}
        ignoreTraits={true}
        ignorePercents={viewMode === 'raw'}
        onChartModeChange={onChartModeChange}
        onZoomChange={onZoomChange}
        zoomLevel={zoomLevel}
        globalZoomLevel={globalZoomLevel}
        colorTheme={colorTheme}
        chartLegendMode={chartLegendMode}
        confidenceLevel={confidenceLevel}
        confidenceLevels={confidenceLevels}
        features={features}
      />
      {shouldShowChartNote(answer) ? (
        <ChartNote error>
          <Popup
            width="400px"
            trigger="click"
            direction="righttop"
            popupOffset={9}
            hideOtherPopupsOnShow
            content={() => TOOLTIP_TEXT}
          >
            <i className="icon-aytmfd-help-v2" />
          </Popup>{' '}
          <b>Attention:</b>&nbsp;This experiment requires at least {MIN_COMPLETES} completes. You are looking at{' '}
          {formatAmounts(answer, ['amount', 'total'])} responses only.
        </ChartNote>
      ) : null}
    </>
  )
}

MaxDiffChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  batches: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  viewMode: PropTypes.oneOf(['varyingProbability', 'probability', 'raw']).isRequired,
  itemsPerSet: PropTypes.number,
  chartMode: PropTypes.string,
  modelChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  onZoomChange: PropTypes.func,
  zoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  onChartReady: PropTypes.func
}

MaxDiffChart.displayName = 'MaxDiffChart'

export default MaxDiffChart
